// advancedSection/index.tsx
//
// Aba "Avançado" da Configuração.
//
// A HISTÓRIA DO `case "Logs"`, em três atos — vale a pena guardá-la inteira,
// porque cada ato explica uma decisão que continua valendo:
//
// 1. TELA BRANCA. O `case` percorria
//    `decryptedInfoToken.organization.companies.flatMap(... =>
//    company.subsidiaries.flatMap(... => subsidiary.logs.map(...)))`. A chave
//    `logs` NUNCA EXISTIU no InfoToken — o backend monta cada filial com
//    `subsidiaryInfo`, `ambients`, `periodTypes` e `periods`, e mais nada
//    (organization.controller.ts). `undefined.map(...)` lança `TypeError` e o
//    React desmontava a árvore inteira: página em branco, sem uma linha de
//    aviso. Era a aba derrubando o aplicativo, não falta de dado.
//
// 2. ESTADO VAZIO HONESTO. A tela passou a DECLARAR a ausência: não existia
//    entidade de log no banco nem rota que expusesse uma, e escrever
//    `subsidiary?.logs ?? []` calaria a exceção ao preço de fingir que lê um
//    ramo inexistente — o dono leria "nenhum log" e concluiria "nada aconteceu
//    no sistema", quando a verdade era "nada é registrado".
//
// 3. O LOG DE VERDADE (aqui). A entidade existe: `alog."Auditoria"`, criada no
//    boot do backend por `garantirEstruturaDeAuditoria` (o par documental é
//    `prisma/sql/008_auditoria.sql`), alimentada pelos pontos de mutação que já
//    existiam — item, configuração de item, loja, ambiente, cadastros de
//    suprimentos, as três decisões de ordem de compra e a troca de endereço do
//    site — e lida por `GET /auditoria`, paginada e SOMENTE PARA
//    ADMINISTRADOR. O `case` virou uma linha: `<LogsDeAuditoria />`.
//
// O InfoToken CONTINUA sem `logs`, e isso é decisão, não pendência: log
// paginado não cabe num JWT que viaja em todo cabeçalho de toda requisição, e
// cresce sem limite. A tela busca por HTTP. O InfoToken segue servindo para o
// que ele é bom — identidade, escopo e o nome das lojas, que a tela usa para
// mostrar "em qual loja" sem uma segunda ida ao servidor.
//
// A tela nova precisa de `useState`/`useEffect`, e este arquivo é um `switch`
// direto no `return`, sem nenhum hook — chamada de hook dentro de `case` é
// proibida pelas Regras dos Hooks. Por isso `LogsDeAuditoria` é um componente
// filho, como `comunicacoesSection/LogDeEnvio.tsx` já é.
//
// As trilhas VIZINHAS continuam citadas dentro da tela, para ela não parecer
// mais completa do que é: asup."MensagemSaida" (tem rota e tela própria em
// Comunicações › Log de envio), asite."HandleAuditoria" e acfg."AlinhamentoLog"
// (nenhuma das duas tem rota). Nenhuma delas é log do sistema.

import React from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/advancedSection";
import LogsDeAuditoria from "./LogsDeAuditoria";

interface AdvancedSectionProps {
  selectedVerticalOption: string;
  isEditing: boolean;
  formData: any;
  decryptedInfoToken: any;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  handleSave: (objectType: any) => void;
  updateState: (newState: any) => void;
}

const AdvancedSection: React.FC<AdvancedSectionProps> = ({
  selectedVerticalOption,
  isEditing,
  formData,
  // `decryptedInfoToken` voltou para a destruturação e tem USO REAL: a tela de
  // Logs o recebe para traduzir o uuid da loja em nome. Enquanto nenhum `case`
  // o usava, ele ficava de fora de propósito — destruturar sem usar vira aviso
  // de lint, e `CI=true` no Render transforma aviso em build quebrado.
  decryptedInfoToken,
  handleInputChange,
  handleSave,
  updateState,
}) => {
  switch (selectedVerticalOption) {
    case "Integrações":
      return (
        <div>
          <h3>Integrações</h3>
          {isEditing ? (
            <>
              <AlfredoStyles.Input
                type="text"
                value={formData.integrationName || ""}
                onChange={(e) => handleInputChange(e, "integrationName")}
                placeholder="Nome da Integração"
              />
              <AlfredoStyles.Input
                type="text"
                value={formData.integrationDescription || ""}
                onChange={(e) => handleInputChange(e, "integrationDescription")}
                placeholder="Descrição"
              />
              <AlfredoStyles.ButtonWrapper>
                <AlfredoStyles.Button onClick={() => handleSave("integration")}>
                  <AlfredoStyles.MaterialIcons>
                    save
                  </AlfredoStyles.MaterialIcons>
                  Salvar
                </AlfredoStyles.Button>
              </AlfredoStyles.ButtonWrapper>
            </>
          ) : (
            <>
              <p>Nome: {formData.integrationName}</p>
              <p>Descrição: {formData.integrationDescription}</p>
              <AlfredoStyles.ButtonWrapper>
                <AlfredoStyles.Button
                  onClick={() => updateState({ isEditing: true })}
                >
                  <AlfredoStyles.MaterialIcons>
                    edit
                  </AlfredoStyles.MaterialIcons>
                  Editar
                </AlfredoStyles.Button>
              </AlfredoStyles.ButtonWrapper>
            </>
          )}
        </div>
      );
    case "Permissões":
      return (
        <div>
          <h3>Permissões</h3>
          {isEditing ? (
            <>
              <AlfredoStyles.Input
                type="text"
                value={formData.permissionName || ""}
                onChange={(e) => handleInputChange(e, "permissionName")}
                placeholder="Nome da Permissão"
              />
              <AlfredoStyles.Input
                type="text"
                value={formData.permissionDescription || ""}
                onChange={(e) => handleInputChange(e, "permissionDescription")}
                placeholder="Descrição"
              />
              <AlfredoStyles.ButtonWrapper>
                <AlfredoStyles.Button onClick={() => handleSave("permission")}>
                  <AlfredoStyles.MaterialIcons>
                    save
                  </AlfredoStyles.MaterialIcons>
                  Salvar
                </AlfredoStyles.Button>
              </AlfredoStyles.ButtonWrapper>
            </>
          ) : (
            <>
              <p>Nome: {formData.permissionName}</p>
              <p>Descrição: {formData.permissionDescription}</p>
              <AlfredoStyles.ButtonWrapper>
                <AlfredoStyles.Button
                  onClick={() => updateState({ isEditing: true })}
                >
                  <AlfredoStyles.MaterialIcons>
                    edit
                  </AlfredoStyles.MaterialIcons>
                  Editar
                </AlfredoStyles.Button>
              </AlfredoStyles.ButtonWrapper>
            </>
          )}
        </div>
      );
    // Sem botão "Editar" e sem botão "Salvar": não há o que editar. Com isso
    // `handleSave("log")` deixa de ser alcançável — ele caía no `default` de
    // configuration/index.tsx, que já recusa com o nome da rota que falta.
    case "Logs":
      return <LogsDeAuditoria decryptedInfoToken={decryptedInfoToken} />;
    case "Plano e Cobrança":
      return (
        <div>
          <h3>Plano e Cobrança</h3>
          <p>Plano atual: </p>
          <p>Status: Ativo</p>
          <AlfredoStyles.ButtonWrapper>
            <AlfredoStyles.Button
              onClick={() => updateState({ isEditing: true })}
            >
              <AlfredoStyles.MaterialIcons>edit</AlfredoStyles.MaterialIcons>
              Editar
            </AlfredoStyles.Button>
          </AlfredoStyles.ButtonWrapper>
        </div>
      );
    default:
      return <div>{`Sessão não encontrada: ${selectedVerticalOption}`}</div>;
  }
};

export default AdvancedSection;
